import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { defer } from '/imports/utils';
import { InputStyled, InputProps } from '/imports/ui/Input';
import { Icon } from '/imports/ui/Icon';

const PhoneInputStyled = styled(InputStyled)`
`;

/**
 * Coerces what was typed into (777) 555-1234, keeping an extension when one is present.
 *
 * Punctuation is DERIVED from the digits rather than accepted from the user, so the stored value has one
 * shape whatever anyone types or pastes. The parens appear only at the fourth digit: adding them at the
 * first would leave a trailing "(123" that backspace cannot get out of cleanly, whereas this way
 * deleting the fourth digit drops straight back to a bare "123".
 * @param value - raw input string
 * @returns the formatted number
 */
const formatValue = (value: string) => {
  // Split at the first x so an extension survives; "ext 12" and "x12" both land here.
  const [main, ...tail] = value.split(/x/i);
  const all = main.replace(/\D/g, '');
  // A pasted "+1 970 848 8200" is eleven digits; without dropping the country code every group shifts
  // by one and it comes out as (197) 084-8820.
  const digits = (all.length === 11 && all.startsWith('1') ? all.slice(1) : all).slice(0, 10);
  const extension = tail.join('x').replace(/\D/g, '');
  const formatted = digits.length <= 3
    ? digits
    : digits.length <= 6
      ? `(${digits.slice(0, 3)}) ${digits.slice(3)}`
      : `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  return extension ? `${formatted} x${extension}` : formatted;
};

/** Phone number text input that coerces what is typed into (777) 555-1234. */
export const PhoneInput = (props: InputProps) => {
  const value = props.value && `${props.value}` || '';
  const hasStartIcon = props.icon && props.iconPlacement !== 'end';
  const [change, setChange] = useState<{ event?: React.ChangeEvent<HTMLInputElement>, value: string }>();
  useEffect(() => {
    if (!change) return;
    const { value, event } = change;
    const _value = formatValue(value);
    props.onChange?.(_value, event);
  }, [change]);
  const clearInput = () => {
    setChange({ value: '' });
    props.onClear?.();
  };
  return (
    <PhoneInputStyled id={props.id} className={`input phone-input ${props.className || ''}${props.hasError ? ' error' : ''}`}>
      {hasStartIcon && <Icon className="icon-start" icon={props.icon as any} />}
      <input
        id={props.inputId}
        className={hasStartIcon ? 'has-start-icon' : ''}
        type="tel"
        placeholder={props.placeholder || ''}
        disabled={props.disabled}
        autoFocus={props.autoFocus}
        value={change ? formatValue(change.value) : (value || '')}
        onChange={(e: any) => setChange({ event: e, value: e.target.value })}
        onClick={(e) => props.onClick?.(e)}
        onFocus={props.onFocus}
        onBlur={props.onBlur}
        onKeyUp={(e) => {
          props.onKeyPress?.(e);
          if (e.key === 'Enter') {
            defer(() => props.onEnter?.(props.value, e));
          }
        }}
        autoComplete={props.autoComplete}
      />
      {
        props.value?.length &&
        !props.disabled &&
        !(props.type === 'color') &&
        <Icon className="clear" icon="cancel" onClick={() => clearInput()} /> || null
      }
    </PhoneInputStyled>
  );
};